/**
 * WidgetCatalog — landing page for the Widget tab.
 *
 * Two grids: Public (release-tagged, everyone) + My Widgets (signed-in user).
 * Bottom: "New Widget" form (title prompt).
 *
 * Click a tile → navigate to /playground/widget/:id (editor).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { widgetApi, widgetInstances } from '../../services/widgetApi';
import { resolveCover } from './covers/coverRender';
import WidgetStudioTabs from './WidgetStudioTabs';
import { TAG_LABELS, TAG_LEGEND } from './tagLabels';

// Light palette — mirrors PlaygroundView.jsx `V` tokens so the Widget tab
// matches the rest of the Apple-light playground. Functional badge colors
// (dev/release) are tuned to read on white. Round-3: switched from
// the previous dark `#0F1115` background.
const COLORS = {
  bg: '#f5f5f7',
  bgCard: '#ffffff',
  bgHover: '#eeeef0',
  border: '#d8d8dc',
  text: '#1d1d1f',
  textDim: '#6e6e73',
  accent: '#5856d6',
  accentDim: '#eeedf5',
  warn: '#ff9500',
  release: '#34c759',
  dev: '#6e6e73',
};

// Tag display names + one-line meanings live in ./tagLabels (shared with the
// editor + use dashboard) — surfaced as badge tooltips here.

function TagBadge({ tag, label }) {
  if (!label) return null;
  const color = COLORS[tag] || COLORS.dev;
  return (
    <span
      title={TAG_LEGEND[tag] || ''}
      style={{
        fontSize: 10, fontWeight: 600, color, padding: '2px 6px',
        border: `1px solid ${color}55`, borderRadius: 4, letterSpacing: '0.04em',
        textTransform: 'uppercase',
        cursor: 'help',
      }}
    >
      {TAG_LABELS[tag] || tag}: {label}
    </span>
  );
}

// Mini-preview thumbnail rendered at the top of each catalog tile.
// Growth feedback: text-only tiles aren't scannable — they couldn't pick
// the right widget from a list. We render the widget's release /
// dev tag (in that order of preference) into a sandboxed iframe at the
// full 480x1040 phone proportions, then CSS-scale 0.5 so the top of the
// widget shows in a 240x170 zone. pointer-events: none means the
// thumbnail can't steal clicks from the tile button.
//
// Auth: if the widget is public and has a release tag, the iframe loads
// the raw URL anonymously. Otherwise we authFetch the bytes ourselves
// and build a blob: URL — same pattern as the editor's main preview.
const THUMB_W = 240;
const THUMB_H = 170;
const THUMB_INNER_W = 480;   // 2x scale
const THUMB_INNER_H = 1040;  // intentionally tall — we only show the top crop
const THUMB_SCALE = 0.5;

// Splice an inline <script> setting `__WIDGET_DATA__` and `__USER_DATA__`
// just inside <head> (or prepended to the doc if <head> is missing). The
// blob:-URL iframe path uses this to hand the widget its runtime payload
// without an authed network fetch. Returns a fresh text/html Blob; the
// original blob is untouched.
async function injectWidgetGlobals(blob, widgetData, userMeta) {
  const text = await blob.text();
  const inject = `<script>window.__WIDGET_DATA__=${JSON.stringify(widgetData)};window.__USER_DATA__=${JSON.stringify(userMeta)};</script>`;
  const headIdx = text.search(/<head[^>]*>/i);
  let merged;
  if (headIdx !== -1) {
    const after = text.indexOf('>', headIdx) + 1;
    merged = text.slice(0, after) + inject + text.slice(after);
  } else {
    merged = inject + text;
  }
  return new Blob([merged], { type: 'text/html; charset=utf-8' });
}

function WidgetThumbnail({ widget }) {
  const tags = widget.tags || {};
  const blobUrlRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  // Build a tag cascade in preference order (release → dev), skipping
  // any tag that doesn't actually have a commit on this widget. We exhaust
  // cover.html across this cascade before falling back to index.html — so
  // a widget whose owner authored cover.html only on `dev` still shows the
  // designed thumbnail in My Widgets, instead of dropping to the empty-state
  // index.html crop at `release`. Public widgets the viewer doesn't own will
  // 401 on dev lookups (api enforces ownership), so the cascade
  // naturally stops at release for those.
  const tagCascade = ['release', 'dev'].filter((t) => tags[t]?.version_label);

  // Tracks which file actually loaded so we can skip the index.html-style
  // scaled-iframe crop when a real cover.html is present (the designer
  // authored cover.html to fit the tile, so no scaling needed).
  const [renderedFile, setRenderedFile] = useState('index.html');

  useEffect(() => {
    if (tagCascade.length === 0) { setSrc(null); setFailed(true); return undefined; }
    let alive = true;
    // Always read via authFetch + blob: URL. The direct /api/widgets/.../files
    // route also serves the release tag anonymously, but the response carries
    // `Content-Security-Policy: ...; sandbox` (no params) which the browser
    // applies on TOP of the iframe sandbox attr — that means even with
    // `sandbox="allow-scripts allow-same-origin"` the iframe still loads in
    // a unique opaque origin and any localStorage / window.parent access
    // throws SecurityError, killing the tile preview. Wrapping the bytes in
    // a blob: URL bypasses the CSP header (blob URLs don't carry response
    // headers) so the iframe sandbox attr is the only thing in play.
    //
    // Prefer cover.html across every tag, then fall back to index.html
    // across every tag. Once every released widget ships a cover.html the
    // scaled-index fallback will go away.
    // Manifest-aware skip: each tag's bundle_manifest.files lists every path
    // committed at that version. Probing those locally avoids an unnecessary
    // network request — and avoids the Chrome devtools auto-logging the 4xx
    // response, which shows up as "benchmarkCapture.js:118 GET ... 404" in
    // the console for widgets that don't have a cover.html. Chrome's auto-log
    // can't be suppressed from JS once the request is made, so the only fix
    // is "don't make the request in the first place."
    const hasFileAt = (t, path) => {
      const files = tags[t]?.bundle_manifest?.files;
      if (!Array.isArray(files)) return true;   // unknown manifest → try fetch
      return files.some((f) => f?.path === path);
    };
    (async () => {
      let blob = null;
      let file = 'cover.html';
      // Preset cover (the DEFAULT — cover_type absent or "preset"): the host
      // renders the tile from a preset reading the widget's example_data. We
      // fetch example_data.json (instead of cover.html) and generate the HTML;
      // legacy widgets with no cover_type thus "migrate" to a preset tile.
      const manifest = tags[tagCascade[0]]?.bundle_manifest || {};
      // Unified cover: cover_json (new scheme) or a legacy preset both build an
      // HTML tile from the widget's example_data; only cover_type:"html" reads
      // the bundle's cover.html file (the fallthrough below).
      const cover = resolveCover(manifest, widget.id);
      if (cover.kind === 'html-doc') {
        let exampleData = { widget_id: widget.id, entries: [] };
        for (const t of tagCascade) {
          try {
            const txt = await widgetApi.readFile(widget.id, 'example_data.json', { tag: t });
            const parsed = JSON.parse(txt);
            if (parsed && typeof parsed === 'object') exampleData = parsed;
            break;
          } catch { /* try next tag, else empty */ }
        }
        if (!alive) return;
        try {
          const coverBlob = new Blob([cover.html], { type: 'text/html; charset=utf-8' });
          const userMeta = { vi_user_id: api.getViUserId?.() || null, name: api.getUserDisplayName?.() || null, appearance: 'auto' };
          // The cover reads __WIDGET_DATA__.title — fall back to the widget's real
          // name so the tile shows it (example_data.json often has no title).
          const coverData = { ...exampleData, title: exampleData.title || widget.title };
          blob = await injectWidgetGlobals(coverBlob, coverData, userMeta);
        } catch { blob = null; }
        if (blob) {
          const url = URL.createObjectURL(blob);
          if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = url;
          setRenderedFile('cover.html');
          setSrc(url);
          setFailed(false);
          return;
        }
      }
      for (const t of tagCascade) {
        if (!hasFileAt(t, 'cover.html')) continue;
        try {
          blob = await widgetApi.readFileAsBlob(widget.id, 'cover.html', { tag: t });
          break;
        } catch { /* try next tag */ }
      }
      if (!blob) {
        for (const t of tagCascade) {
          if (!hasFileAt(t, 'index.html')) continue;
          try {
            blob = await widgetApi.readFileAsBlob(widget.id, 'index.html', { tag: t });
            file = 'index.html';
            break;
          } catch { /* try next tag */ }
        }
      }
      if (!blob) {
        if (alive) { setSrc(null); setFailed(true); }
        return;
      }
      if (!alive) return;

      // The cover.html contract grants the same runtime injection as
      // index.html — __USER_DATA__ for personalization, __WIDGET_DATA__
      // for live counts. The catalog tile doesn't have the per-widget
      // entries/sessions fetched (would be N fetches per render), so we
      // inject the slim shape (empty arrays, widget_id, settings) and
      // let cover.html render its empty-state path. The studio's User Data
      // runtime injects the full payload where it matters. index.html tiles get the same
      // shim so a legacy preview doesn't break on missing globals.
      try {
        const userMeta = {
          vi_user_id: api.getViUserId?.() || null,
          name: api.getUserDisplayName?.() || null,
          appearance: 'auto',
        };
        const widgetData = { widget_id: widget.id, entries: [], sessions: [], settings: {} };
        blob = await injectWidgetGlobals(blob, widgetData, userMeta);
      } catch { /* keep original blob on injection failure */ }

      const url = URL.createObjectURL(blob);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = url;
      setRenderedFile(file);
      setSrc(url);
      setFailed(false);
    })();
    return () => {
      alive = false;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
    // tagCascade is derived from widget.tags; stringify so the effect
    // only re-runs when the actual tag set changes, not on every render.
  }, [widget.id, widget.is_public, tagCascade.join(',')]);

  if (!src || failed) {
    return (
      <div style={{
        width: THUMB_W, height: THUMB_H, borderRadius: 12,
        background: 'linear-gradient(135deg, #eeedf5 0%, #d4d3e3 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: COLORS.textDim, fontSize: 13, fontWeight: 600, padding: 12,
        textAlign: 'center', overflow: 'hidden',
      }}>
        {widget.title || '(untitled)'}
      </div>
    );
  }

  const isCover = renderedFile === 'cover.html';
  return (
    <div style={{
      width: THUMB_W, height: THUMB_H, overflow: 'hidden',
      borderRadius: 12, background: '#F2F2F7', pointerEvents: 'none',
      position: 'relative',
    }}>
      {/* Sandbox: `allow-scripts` only (no `allow-same-origin`) — the
          thumbnail is a read-only preview, doesn't need storage / parent
          access. Chrome warns when both flags are set together
          ("iframe can escape its sandboxing"); dropping the same-origin
          flag silences that warning and tightens the sandbox. Widgets
          that try to read localStorage will silently fail — that's fine
          here, the tile is just rendering the visual.

          When cover.html is present the designer authored it at tile size
          (THUMB_W × THUMB_H), so render 1:1 with no scaling. The legacy
          index.html path renders at phone proportions and gets scaled 0.5
          to show a top crop. */}
      <iframe
        src={src}
        title={`thumb-${widget.id}`}
        sandbox="allow-scripts"
        scrolling="no"
        style={isCover ? {
          width: THUMB_W, height: THUMB_H, border: 0, pointerEvents: 'none',
        } : {
          width: THUMB_INNER_W,
          height: THUMB_INNER_H,
          border: 0,
          transform: `scale(${THUMB_SCALE})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

function WidgetTile({ widget, isMine, onClick, onDelete, onUse, onDownload }) {
  const tags = widget.tags || {};
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  // Show the "⋯" menu if there's at least one action on it. "Instantiate"
  // is universally available; Download / Delete are only attached on the My
  // Widgets grid.
  const hasMenu = !!onUse || !!onDelete || !!onDownload;
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(widget)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(widget); } }}
      style={{
        position: 'relative',
        background: COLORS.bgCard,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        cursor: 'pointer',
        textAlign: 'left',
        color: COLORS.text,
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = COLORS.bgHover; e.currentTarget.style.borderColor = COLORS.accent + '66'; }}
      onMouseLeave={e => { e.currentTarget.style.background = COLORS.bgCard; e.currentTarget.style.borderColor = COLORS.border; }}
    >
      {isMine && (
        <span
          title="You own this widget"
          style={{
            position: 'absolute', top: 8, right: 38, zIndex: 2,
            background: COLORS.accent + '22', color: COLORS.accent,
            fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', padding: '2px 8px',
            borderRadius: 999, border: `1px solid ${COLORS.accent}55`,
          }}
        >Mine</span>
      )}
      {!isMine && widget.shared && (
        <span
          title="A teammate's private widget — shared with admins. You can open and edit it."
          style={{
            position: 'absolute', top: 8, right: 38, zIndex: 2,
            background: COLORS.warn + '22', color: COLORS.warn,
            fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', padding: '2px 8px',
            borderRadius: 999, border: `1px solid ${COLORS.warn}55`,
          }}
        >Shared · private</span>
      )}
      {hasMenu && (
        <div ref={menuRef} style={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}>
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
            onKeyDown={(e) => e.stopPropagation()}
            title="More"
            aria-label="More actions"
            style={{
              background: 'rgba(255,255,255,0.92)', color: COLORS.text,
              border: `1px solid ${COLORS.border}`, borderRadius: 999,
              width: 26, height: 26, fontSize: 14, lineHeight: '1',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ⋯
          </button>
          {menuOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', top: 30, right: 0, minWidth: 160,
                background: COLORS.bgCard, border: `1px solid ${COLORS.border}`,
                borderRadius: 8, boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                padding: 4,
              }}
            >
              {onUse && (
                <button
                  onClick={() => { setMenuOpen(false); onUse(widget); }}
                  style={{
                    width: '100%', textAlign: 'left',
                    background: 'transparent', color: COLORS.text,
                    border: 'none', borderRadius: 6, padding: '6px 10px',
                    fontSize: 12, cursor: 'pointer',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = COLORS.bgHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {widget.widget_type === 'template' ? 'Instantiate' : 'Use'}
                </button>
              )}
              {onDownload && (
                <button
                  onClick={() => { setMenuOpen(false); onDownload(widget); }}
                  style={{
                    width: '100%', textAlign: 'left',
                    background: 'transparent', color: COLORS.text,
                    border: 'none', borderRadius: 6, padding: '6px 10px',
                    fontSize: 12, cursor: 'pointer',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = COLORS.bgHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Download ZIP
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => { setMenuOpen(false); onDelete(widget); }}
                  style={{
                    width: '100%', textAlign: 'left',
                    background: 'transparent', color: '#b3261e',
                    border: 'none', borderRadius: 6, padding: '6px 10px',
                    fontSize: 12, cursor: 'pointer',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fff1f0'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Delete…
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <WidgetThumbnail widget={widget} />
      <div style={{ fontSize: 14, fontWeight: 600 }}>
        {widget.title || <span style={{ color: COLORS.textDim }}>(untitled)</span>}
      </div>
      <div style={{ fontSize: 11, color: COLORS.textDim, flex: 1 }}>
        {widget.description || (widget.slug ? `slug: ${widget.slug}` : ' ')}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {tags.dev?.version_label && <TagBadge tag="dev" label={tags.dev.version_label} />}
        {tags.release?.version_label && <TagBadge tag="release" label={tags.release.version_label} />}
      </div>
      <div style={{ fontSize: 9, color: COLORS.textDim, letterSpacing: '0.04em', lineHeight: 1.3 }}>
        {TAG_LABELS.dev} = only you · {TAG_LABELS.release} = everyone
      </div>
    </div>
  );
}

// Confirm-delete modal. Used by the catalog tile menu and the editor
// header overflow menu so the copy + button styling stay in lock-step.
export function DeleteWidgetModal({ widget, onCancel, onConfirm, busy, error }) {
  if (!widget) return null;
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#ffffff', borderRadius: 12, padding: 20,
          width: 360, maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.18)',
          color: COLORS.text,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
          Delete {widget.title || '(untitled)'} from the server?
        </div>
        <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.5, marginBottom: 16 }}>
          This is a complete deletion of this widget on the server: every
          version and file is wiped, it leaves the catalog, and it disappears
          for <strong>all users</strong> who use it. This can’t be undone.
        </div>
        {error && (
          <div style={{ fontSize: 11, color: '#b3261e', marginBottom: 12 }}>{error}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              background: 'transparent', color: COLORS.text,
              border: `1px solid ${COLORS.border}`, borderRadius: 6,
              padding: '6px 12px', fontSize: 12, cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              background: '#b3261e', color: '#ffffff', border: 'none',
              borderRadius: 6, padding: '6px 12px', fontSize: 12,
              fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? 'Deleting…' : 'Delete for everyone'}
          </button>
        </div>
      </div>
    </div>
  );
}

const slugifyTitle = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);

function CreateWidgetForm({ onCreated, widgetType = 'standard' }) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = useCallback(async (e) => {
    e?.preventDefault();
    if (!title.trim() || !slug || busy) return;
    setBusy(true); setError(null);
    try {
      const widget = await widgetApi.create({
        slug,
        title: title.trim(),
        description: description.trim() || null,
        widget_type: widgetType,
      });
      onCreated(widget);
      setTitle(''); setSlug(''); setSlugTouched(false); setDescription('');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }, [title, slug, description, busy, onCreated, widgetType]);

  return (
    <form
      onSubmit={submit}
      style={{
        background: COLORS.bgCard,
        border: `1px dashed ${COLORS.border}`,
        borderRadius: 12,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
        {widgetType === 'template' ? '+ New Template' : '+ New Widget'}
      </div>
      <input
        type="text"
        value={title}
        onChange={e => {
          setTitle(e.target.value);
          if (!slugTouched) setSlug(slugifyTitle(e.target.value));
        }}
        placeholder={widgetType === 'template' ? 'Template name (e.g. Folder)' : 'Widget name (e.g. Wardrobe Tracker)'}
        style={{
          background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`,
          borderRadius: 8, padding: '10px 12px', fontSize: 13, outline: 'none',
        }}
      />
      <input
        type="text"
        value={slug}
        onChange={e => { setSlugTouched(true); setSlug(slugifyTitle(e.target.value) || e.target.value.toLowerCase()); }}
        placeholder="id (e.g. wardrobe-tracker) — permanent, same across envs"
        title="The widget's permanent id — lowercase letters, digits, hyphens. Must be globally unique."
        style={{
          background: COLORS.bg, color: COLORS.textDim, border: `1px solid ${COLORS.border}`,
          borderRadius: 8, padding: '10px 12px', fontSize: 12, outline: 'none',
          fontFamily: 'ui-monospace, monospace',
        }}
      />
      <input
        type="text"
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="One-line description (optional)"
        style={{
          background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`,
          borderRadius: 8, padding: '10px 12px', fontSize: 12, outline: 'none',
        }}
      />
      {error && <div style={{ color: '#b3261e', fontSize: 11 }}>{error}</div>}
      <button
        type="submit"
        disabled={!title.trim() || !slug || busy}
        style={{
          background: title.trim() && slug && !busy ? COLORS.accent : COLORS.border,
          color: title.trim() && slug && !busy ? '#ffffff' : COLORS.textDim,
          border: 'none', borderRadius: 8, padding: '10px 14px', fontSize: 12,
          fontWeight: 600, cursor: title.trim() && slug && !busy ? 'pointer' : 'not-allowed',
          transition: 'background 0.15s',
        }}
      >
        {busy ? 'Creating…' : 'Create and open editor'}
      </button>
    </form>
  );
}

function GridSection({ title, items, onItemClick, onItemDelete, onItemUse, onItemDownload, emptyHint, mineIds, canManageAll = false, noun = 'widget' }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, margin: 0 }}>{title}</h2>
        <span style={{ fontSize: 11, color: COLORS.textDim }}>{items.length} {noun}{items.length === 1 ? '' : 's'}</span>
      </header>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: COLORS.textDim, padding: '20px 4px' }}>{emptyHint}</div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(264px, 1fr))',
          gap: 12,
        }}>
          {items.map(w => {
            const owned = !!mineIds && mineIds.has(w.id);
            // Owner-or-admin: the backend authorizes admin delete/download
            // for ANY widget (#2295). `canManageAll` = viewer is admin
            // (role from /api/auth/me); `shared` rows are admin-only anyway
            // (include_shared) so they stay actionable even before the role
            // fetch resolves. Everyone else only manages their own tiles —
            // no dead Delete…/Download items on the rest.
            const managed = owned || !!w.shared || canManageAll;
            return (
              <WidgetTile
                key={w.id}
                widget={w}
                isMine={owned}
                onClick={onItemClick}
                onDelete={managed ? onItemDelete : undefined}
                onUse={onItemUse}
                onDownload={managed ? onItemDownload : undefined}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function WidgetCatalog({ widgetType = 'standard' }) {
  const navigate = useNavigate();
  // Remember the active tab so /playground/widget/studio (bare) lands the
  // user back where they last were.
  useEffect(() => {
    try { localStorage.setItem('vi:widget-studio-tab', widgetType === 'template' ? 'template' : 'standard'); }
    catch { /* localStorage blocked — non-fatal */ }
  }, [widgetType]);
  const [publicWidgets, setPublicWidgets] = useState([]);
  const [myWidgets, setMyWidgets] = useState([]);
  const [error, setError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // widget obj
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  // Install flow — toast surfaces success/error so the user gets a beat
  // of feedback before the route changes (the navigate() below replaces
  // the catalog in ~150ms once the POST resolves).
  const [installBusy, setInstallBusy] = useState(null); // widget id currently installing
  const [toast, setToast] = useState(null); // { kind: 'ok'|'err', msg }
  // Admins may delete/download ANY widget (backend enforces owner-or-admin,
  // #2295) — the role decides whether non-owned tiles get those menu items.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let alive = true;
    api.getMe().then((me) => { if (alive) setIsAdmin(me?.role === 'admin'); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const reload = useCallback(async () => {
    setError(null);
    try {
      // widgetApi.jsonOrThrow logs (rate-limited) on its own — don't double
      // up the console with our catch-block warns. We just swallow the
      // error here so a 401 during the auth-sync window doesn't break
      // listPublic alongside it.
      const [pub, mine] = await Promise.all([
        widgetApi.listPublic(50, 0, { widget_type: widgetType }).catch(() => ({ widgets: [] })),
        widgetApi.listMine({ widget_type: widgetType }).catch(() => ({ widgets: [] })),
      ]);
      setPublicWidgets(pub.widgets || []);
      setMyWidgets(mine.widgets || []);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [widgetType]);

  useEffect(() => { reload(); }, [reload]);

  const openEditor = useCallback((widget) => {
    navigate(`/playground/widget/studio/${widget.id}`);
  }, [navigate]);

  const onCreated = useCallback((widget) => {
    navigate(`/playground/widget/studio/${widget.id}`);
  }, [navigate]);

  const onTileDelete = useCallback((widget) => {
    setDeleteError(null);
    setPendingDelete(widget);
  }, []);

  const showToast = useCallback((kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast((cur) => (cur && cur.msg === msg ? null : cur)), 4000);
  }, []);

  const onTileDownload = useCallback(async (widget) => {
    // Prefer the most-recent tag the user actually published: release > dev.
    // The bundle endpoint will 404 if the chosen tag has no zip, so fall
    // through. Catch any final error and toast so the user knows.
    const available = ['release', 'dev'].filter(t => widget?.tags?.[t]?.version_label);
    const candidates = available.length ? available : ['dev'];
    const slug = widget.slug
      || widget.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      || widget.id;
    let lastErr = null;
    for (const tag of candidates) {
      try {
        const label = widget.tags?.[tag]?.version_label || tag;
        const filename = `${slug}-${label}.zip`;
        await widgetApi.downloadBundle(widget.id, { tag, filename });
        showToast('ok', `Downloaded ${filename}`);
        return;
      } catch (e) {
        lastErr = e;
      }
    }
    showToast('err', `Download failed: ${lastErr?.message || lastErr}`);
  }, [showToast]);

  const onTileUse = useCallback(async (widget) => {
    if (installBusy) return;
    setInstallBusy(widget.id);
    try {
      // Instantiate the widget (idempotent for standard widgets, always-new
      // for templates) then navigate to the instance's dashboard. For
      // templates we ask for a `title` so the user can distinguish
      // multiple instances side by side ("My Birds" vs "My Mushrooms").
      let title;
      if (widget.widget_type === 'template') {
        title = window.prompt('Name this instance:', widget.title || 'My Folder');
        if (!title) { setInstallBusy(null); return; }
      }
      const instance = await widgetInstances.instantiate(widget.id, { title });
      const label = title || widget.title || 'widget';
      showToast('ok', `Opening "${label}"…`);
      navigate(`/playground/widget/studio/${widget.id}?data=user&instance=${encodeURIComponent(instance.instance_id)}`);
    } catch (e) {
      showToast('err', `Open failed: ${e?.message || e}`);
    } finally {
      setInstallBusy(null);
    }
  }, [installBusy, navigate, showToast]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await widgetApi.deleteWidget(pendingDelete.id);
      // Drop from local state without a refetch — the catalog scroll
      // position survives that way and growth doesn't see a flicker.
      setMyWidgets((prev) => prev.filter((w) => w.id !== pendingDelete.id));
      setPublicWidgets((prev) => prev.filter((w) => w.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (e) {
      setDeleteError(e.message || String(e));
    } finally {
      setDeleteBusy(false);
    }
  }, [pendingDelete]);

  return (
    <div style={{
      // .pg-layout-main is overflow: hidden + fixed clientHeight, so the
      // catalog owns its own internal scroll. height: 100% pins it to the
      // parent; overflowY: auto + minHeight: 0 lets the grid scroll while
      // .pg-layout-main itself does NOT overflow (which would break other
      // playground pages).
      padding: '24px 32px',
      height: '100%', minHeight: 0, overflowY: 'auto',
      background: COLORS.bg, color: COLORS.text,
      display: 'flex', flexDirection: 'column', gap: 28,
    }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <WidgetStudioTabs active={widgetType} colors={COLORS} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={reload}
            style={{
              background: 'transparent', color: COLORS.textDim, border: `1px solid ${COLORS.border}`,
              borderRadius: 6, padding: '6px 10px', fontSize: 11, cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div style={{ padding: 12, background: '#fff1f0', border: '1px solid #ffd0cc', color: '#b3261e', borderRadius: 8, fontSize: 12 }}>
          {error}
        </div>
      )}

      <CreateWidgetForm onCreated={onCreated} widgetType={widgetType} />

      {(() => {
        // Per goal + Widget Rules: "Private/Public Widgets" on the Standard
        // tab, "Private/Public Templates" on the Templates tab. Mine = same
        // user id across the public catalog → "Mine" badge.
        const isTpl = widgetType === 'template';
        const noun = isTpl ? 'template' : 'widget';
        const nounCap = isTpl ? 'Template' : 'Widget';
        // `shared` cards (admin-only: teammates' private widgets) are owned by
        // someone else, so they're NOT in mineIds — keeps delete owner-only.
        const mineIds = new Set(myWidgets.filter((w) => !w.shared).map((w) => w.id));
        // Private section = NON-public widgets only (mine + the shared set).
        // A widget that's already public lists ONCE — under Public — its dev
        // pin rides on that same card; duplicating it here as a "private" row
        // was just noise.
        const privateWidgets = myWidgets.filter((w) => !w.is_public);
        return (
          <>
            {/* Delete/download are owner-or-admin (GridSection gates per
                tile): the backend has allowed admins to delete/download any
                widget since #2295, and `shared` cards only render for
                admins — the old owner-only wrapper here silently no-oped
                Delete… on them. */}
            <GridSection
              title={`Private ${nounCap}s`}
              noun={noun}
              items={privateWidgets}
              onItemClick={openEditor}
              onItemDelete={onTileDelete}
              onItemUse={onTileUse}
              onItemDownload={onTileDownload}
              mineIds={mineIds}
              canManageAll={isAdmin}
              emptyHint={`You haven't created any private ${noun}s yet. Use the form above to start.`}
            />

            <GridSection
              title={`Public ${nounCap}s`}
              noun={noun}
              items={publicWidgets}
              onItemClick={openEditor}
              onItemUse={onTileUse}
              onItemDelete={onTileDelete}
              onItemDownload={onTileDownload}
              mineIds={mineIds}
              canManageAll={isAdmin}
              emptyHint={`No public ${noun}s yet — be the first to release one.`}
            />
          </>
        );
      })()}

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            background: toast.kind === 'err' ? '#fff1f0' : '#1d1d1f',
            color: toast.kind === 'err' ? '#b3261e' : '#ffffff',
            border: toast.kind === 'err' ? '1px solid #ffd0cc' : 'none',
            borderRadius: 10, padding: '10px 16px', fontSize: 12, fontWeight: 500,
            boxShadow: '0 8px 28px rgba(0,0,0,0.12)', zIndex: 1100,
            maxWidth: 480,
          }}
        >
          {toast.msg}
        </div>
      )}

      <DeleteWidgetModal
        widget={pendingDelete}
        busy={deleteBusy}
        error={deleteError}
        onCancel={() => { if (!deleteBusy) { setPendingDelete(null); setDeleteError(null); } }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
